import React, { useState } from 'react';
import { Button, Input } from 'edozvola-frontend-lib/dist/ui/components';
import FinalReportOfSupervisingEngineerForm from 'app/edozvola/cases/case-technical-inspection/final-report-form';
import WrittenStatementForm from 'app/edozvola/cases/case-technical-inspection/written-statement-form';
import FinalReportTable from 'app/edozvola/cases/case-technical-inspection/final-report-table';
import WrittenStatementTable from 'app/edozvola/cases/case-technical-inspection/written-statement-table';
import { FormikProvider, useFormik } from 'formik';
import { technicalInspectionInitial } from 'app/edozvola/cases/case-technical-inspection/model/case-technical-inspection.model';
import { Col, Row } from 'reactstrap';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import { useParams } from 'react-router-dom';
import {
  existsReportsOrStatementsByBuildingDiaryCodeMutation,
  fetchReportsAndStatementsMutation,
  getTechnicalInspectionQuery,
  saveTechnicalInspectionQuery,
} from './service/case-technical-inspection.queries';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import { useDispatch } from 'react-redux';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { getCaseContext } from 'app/shared/util/context-util';
import { ConfirmationModal } from 'app/shared/modal/confirmation-modal';

const CaseTechnicalInspection = () => {
  const { id: uuid } = useParams<{ id: string }>();
  const { sidebarItemPair } = getCaseContext();
  const dispatch = useDispatch();

  const [idFinalReport, setIdFinalReport] = useState<string | null>(null);
  const [finalReportReadOnly, setFinalReportReadOnly] = useState<boolean>();
  const [idWrittenStatement, setIdWrittenStatement] = useState<string | null>(null);
  const [writtenStatementReadOnly, setWrittenStatementReadOnly] = useState<boolean>(null);
  const [buildingDiaryCodeToFetchReports, setBuildingDiaryCodeToFetchReports] = useState<string>('');
  const [deleteExistingReportsByBuildingDiaryCodeModalOpened, setDeleteExistingReportsByBuildingDiaryCodeModalOpened] =
    useState<boolean>(false);

  const { data: technicalInspectionData, isLoading: technicalInspectionLoading } = getTechnicalInspectionQuery(uuid);
  const { mutate: fetchReportsAndStatements, isPending: fetchingReportsAndStatements } = fetchReportsAndStatementsMutation(
    uuid,
    setBuildingDiaryCodeToFetchReports,
  );
  const { mutate: checkReportsExist, isPending: checkingReportsExist } = existsReportsOrStatementsByBuildingDiaryCodeMutation(
    uuid,
    setDeleteExistingReportsByBuildingDiaryCodeModalOpened,
    fetchReportsAndStatements,
  );

  const formik = useFormik({
    initialValues: technicalInspectionData || technicalInspectionInitial,
    onSubmit: values => saveTechnicalInspection(values),
    enableReinitialize: true,
    validateOnMount: true,
  });

  const { mutate: saveTechnicalInspection, isPending: savingTechnicalInspection } = saveTechnicalInspectionQuery(uuid, formik.resetForm);

  const openFinalReportForm = () => {
    setIdFinalReport('');
    setFinalReportReadOnly(false);
  };

  const openWrittenStatementForm = () => {
    setIdWrittenStatement('');
    setWrittenStatementReadOnly(false);
  };

  return (
    <>
      <div className={'d-flex justify-content-end'}>
        <div>
          <Label labelText={'Šifra građevinskog dnevnika'} />
          <div className={'d-flex justify-content-between flex-row'}>
            <Input value={buildingDiaryCodeToFetchReports}
                   disabled={sidebarItemPair?.readOnly}
                   onChange={event => setBuildingDiaryCodeToFetchReports(event.target.value)} />
            <Button
              label={'Dohvati podatke'}
              color={'info'}
              prependIcon={'plus'}
              size={'lg'}
              onClick={() => checkReportsExist(buildingDiaryCodeToFetchReports)}
              disabled={sidebarItemPair?.readOnly || buildingDiaryCodeToFetchReports === ''}
            />
          </div>
        </div>
      </div>
      <hr />
      <div className={'mt-5 d-flex justify-content-between'}>
        <div className={'form-header'}>Završna izvješća nadzornih inženjera</div>
        {!sidebarItemPair?.readOnly && (
          <>
            <div className={'d-flex justify-content-end pb-4 gap-2'}>
              <div>
                <Button label={'Dodaj završno izvješće'} color={'info'} prependIcon={'plus'} size={'lg'} onClick={openFinalReportForm} />
              </div>
            </div>
          </>
        )}
      </div>
      <div className={'mt-3'}>
        <FinalReportTable setFinalReport={setIdFinalReport} setReadOnly={setFinalReportReadOnly} />
      </div>
      {idFinalReport !== null && (
        <FinalReportOfSupervisingEngineerForm
          idFinalReport={idFinalReport}
          setIdFinalReport={setIdFinalReport}
          readOnly={finalReportReadOnly}
        />
      )}
      <hr />
      <div className={'d-flex justify-content-between'}>
        <div className={'form-header'}>Pisane izjave izvođača</div>
        {!sidebarItemPair?.readOnly && (
          <>
            <div className={'d-flex justify-content-end pb-4 gap-2'}>
              <div>
                <Button label={'Dodaj pisanu izjavu'} color={'info'} prependIcon={'plus'} size={'lg'} onClick={openWrittenStatementForm} />
              </div>
            </div>
          </>
        )}
      </div>
      <div className={'mt-3'}>
        <WrittenStatementTable setWrittenStatement={setIdWrittenStatement} setReadOnly={setWrittenStatementReadOnly} />
      </div>
      {idWrittenStatement !== null && (
        <WrittenStatementForm
          idWrittenStatement={idWrittenStatement}
          setIdWrittenStatement={setIdWrittenStatement}
          readOnly={writtenStatementReadOnly}
        />
      )}
      <FormikProvider value={formik}>
        <FormikValueObserver
          onChange={dirty => {
            if (dirty.dirty) {
              dispatch(setFormChanged(true));
            } else {
              dispatch(initCaseFormChange());
            }
          }}
        />
        <hr />
        <div className={'form-header'}>Ostali podaci</div>
        <div className={'mt-5'}>
          <Row>
            <Col md={6}>
              <Label labelText={'Troškovi tehničkog pregleda'} />
              <Input
                disabled={sidebarItemPair?.readOnly}
                value={formik.values.technicalInspectionCost}
                maxLength={100}
                onChange={event => formik.setFieldValue('technicalInspectionCost', event.target.value)}
              />
            </Col>
            <Col md={6}>
              <Label labelText={'Zabilješka s tehničkog pregleda / završno mišljenje'} />
              <Input
                disabled={sidebarItemPair?.readOnly}
                value={formik.values.technicalInspectionNote}
                maxLength={100}
                onChange={event => formik.setFieldValue('technicalInspectionNote', event.target.value)}
              />
            </Col>
          </Row>
        </div>
      </FormikProvider>
      {!sidebarItemPair?.readOnly && (
        <div className={'case-save-button'}>
          <Button
            label={'Spremi'}
            size={'lg'}
            disabled={!formik.dirty || !formik.isValid}
            isLoading={savingTechnicalInspection}
            color={'info'}
            onClick={() => formik.handleSubmit()}
          />
        </div>
      )}
      <ConfirmationModal
        header={'Dohvati podatke'}
        modalOpened={deleteExistingReportsByBuildingDiaryCodeModalOpened}
        onCancel={() => setDeleteExistingReportsByBuildingDiaryCodeModalOpened(false)}
        bodyMessage={
          'Dohvatom podataka obrisat će se prethodno dodana završna izvješća i pisane izjave za unesenu šifru građevinskog dnevnika! Želite li nastaviti s dohvatom?'
        }
        onConfirm={() => {
          setDeleteExistingReportsByBuildingDiaryCodeModalOpened(false);
          fetchReportsAndStatements(buildingDiaryCodeToFetchReports);
        }}
      />
    </>
  );
};

export default CaseTechnicalInspection;
